import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { AudioFile, Close, CloudUpload, MusicNote } from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { createSnack } from 'common/utils/general';
import uiSlice from 'store/modules/ui';
import {
  ADMIN_PLAY_MUSIC_TRACKS,
  SAVE_PLAY_MUSIC_TRACK,
  UPLOAD_PLAY_LIBRARY_MEDIA
} from 'api/graphql/playMusic';
import { formatDuration } from 'components/play/workoutEstimate';

const CATEGORIES = ['relax', 'focus', 'nature', 'instrumental'];

/**
 * OTRIX Play (admin) — crear/editar una pista de la librería de música. El audio se sube
 * DE INMEDIATO al elegirlo (streaming a S3) y la duración se lee sola de los metadatos:
 * el admin no espera ni escribe segundos a mano.
 */
function PlayMusicTrackModal({ open, track = null, onClose }) {
  const { t } = useTranslation(['play']);
  const dispatch = useDispatch();
  const fileRef = useRef(null);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('relax');
  const [isActive, setIsActive] = useState(true);
  const [fileUrl, setFileUrl] = useState('');
  const [duration, setDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [uploadMedia] = useMutation(UPLOAD_PLAY_LIBRARY_MEDIA);
  const [saveTrack, { loading: saving }] = useMutation(SAVE_PLAY_MUSIC_TRACK, {
    refetchQueries: [{ query: ADMIN_PLAY_MUSIC_TRACKS }]
  });

  useEffect(() => {
    if (open) {
      setTitle(track?.title || '');
      setCategory(track?.category || 'relax');
      setIsActive(track ? Boolean(track.is_active) : true);
      setFileUrl(track?.file_url || '');
      setDuration(track?.duration_seconds || 0);
      setUploading(false);
      setSubmitted(false);
    }
  }, [open, track]);

  const notify = (variant, message) =>
    dispatch(uiSlice.actions.enqueueSnackbar(createSnack({ key: `music-${Date.now()}`, variant, message })));

  const readDuration = (file) =>
    new Promise((resolve) => {
      const url = URL.createObjectURL(file);
      const audio = new Audio();
      audio.preload = 'metadata';
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(audio.duration) ? Math.round(audio.duration) : 0);
      };
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(0);
      };
      audio.src = url;
    });

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const [{ data }, seconds] = await Promise.all([
        uploadMedia({ variables: { file } }),
        readDuration(file)
      ]);
      setFileUrl(data?.uploadPlayLibraryMedia?.url || '');
      setDuration(seconds);
      if (!title.trim()) setTitle(file.name.replace(/\.[^.]+$/, ''));
    } catch {
      notify('error', t('musicAdmin.uploadError', { ns: 'play', defaultValue: 'No pudimos subir el audio. Intenta de nuevo.' }));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!title.trim() || !fileUrl) return;

    try {
      await saveTrack({
        variables: {
          input: {
            id: track?.id || null,
            title: title.trim(),
            category,
            duration_seconds: duration,
            file_url: fileUrl,
            is_active: isActive,
            position: track?.position || 0
          }
        }
      });
      notify('success', t('musicAdmin.saved', { ns: 'play', defaultValue: 'Pista guardada.' }));
      onClose();
    } catch {
      notify('error', t('musicAdmin.saveError', { ns: 'play', defaultValue: 'No pudimos guardar la pista. Intenta de nuevo.' }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main'
        }
      }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <MusicNote sx={{ color: 'gold.main' }} />
          <Typography variant="h6" fontWeight={800}>
            {track
              ? t('musicAdmin.editTitle', { ns: 'play', defaultValue: 'Editar pista' })
              : t('musicAdmin.newTitle', { ns: 'play', defaultValue: 'Nueva pista' })}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }} aria-label={t('recovery.close', { ns: 'play' })}>
          <Close fontSize="small" />
        </IconButton>
      </Stack>

      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* Audio primero: sin esperar a llenar datos. */}
          <Box
            sx={{
              p: 2,
              borderRadius: '12px',
              border: '1px dashed',
              borderColor: submitted && !fileUrl ? 'gold.main' : 'hairline.main',
              backgroundColor: 'surface.overlay',
              textAlign: 'center'
            }}>
            <input ref={fileRef} type="file" accept="audio/*" hidden onChange={onPickFile} />
            {fileUrl ? (
              <Stack spacing={0.5} alignItems="center">
                <AudioFile sx={{ color: 'gold.main' }} />
                <Typography variant="caption" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {duration ? formatDuration(duration) : t('musicAdmin.ready', { ns: 'play', defaultValue: 'Audio listo' })}
                </Typography>
                <Button size="small" onClick={() => fileRef.current?.click()} disabled={uploading} sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 700 }}>
                  {t('musicAdmin.replace', { ns: 'play', defaultValue: 'Reemplazar audio' })}
                </Button>
              </Stack>
            ) : (
              <Button
                startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUpload />}
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                sx={{ color: 'gold.main', textTransform: 'none', fontWeight: 800 }}>
                {uploading
                  ? t('musicAdmin.uploading', { ns: 'play', defaultValue: 'Subiendo…' })
                  : t('musicAdmin.pick', { ns: 'play', defaultValue: 'Subir audio (MP3, M4A, WAV, OGG)' })}
              </Button>
            )}
            {submitted && !fileUrl && (
              <Typography variant="caption" sx={{ color: 'gold.main', display: 'block', mt: 0.5 }}>
                {t('musicAdmin.fileRequired', { ns: 'play', defaultValue: 'Sube el audio de la pista.' })}
              </Typography>
            )}
          </Box>

          <TextField
            label={t('musicAdmin.title', { ns: 'play', defaultValue: 'Título' })}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            error={submitted && !title.trim()}
            helperText={submitted && !title.trim() ? t('musicAdmin.titleRequired', { ns: 'play', defaultValue: 'Requerido' }) : ''}
            fullWidth
          />

          <TextField
            select
            label={t('musicAdmin.category', { ns: 'play', defaultValue: 'Categoría' })}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth>
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {t(`musicAdmin.categories.${c}`, { ns: 'play', defaultValue: c })}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2" fontWeight={700}>
              {t('musicAdmin.active', { ns: 'play', defaultValue: 'Visible para los clientes' })}
            </Typography>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="secondary" />
          </Stack>

          <Button fullWidth onClick={handleSave} disabled={saving || uploading} sx={{ ...premiumButtonSx, py: 1.2 }}>
            {saving
              ? t('musicAdmin.saving', { ns: 'play', defaultValue: 'Guardando…' })
              : t('musicAdmin.save', { ns: 'play', defaultValue: 'Guardar pista' })}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

PlayMusicTrackModal.propTypes = {
  open: PropTypes.bool.isRequired,
  track: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

export default PlayMusicTrackModal;
